import { z } from "zod";
import { EffectSchema, type Effect, type EffectKind } from "./effects";

/*
  ITEMS. The Dojo Shop's nine items (brief, section 5), as data. An item is
  owned once, for good, and most have three levels: buying gives level 1 and
  the shop then offers the next. Active items are a button in battle, once a
  battle; passive items are always on while worn. Effects use the kinds in
  effects.ts, shared with special moves.

  Bump SHOP_VERSION when prices or effects change. A purchase carries the
  version and price it was made at, so a retune never rewrites what was paid,
  and the database keeps every version's prices (shop_items) to check
  purchases against.
*/

export const SHOP_VERSION = 1;
export const MAX_LEVEL = 3;

export const ITEM_KINDS = ["active", "passive"] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];

export const ITEM_IDS = [
  "slow-time",
  "smoke-bomb",
  "double-strike",
  "shield-scroll",
  "hint-scroll",
  "swift-sandals",
  "iron-headband",
  "lucky-charm",
  "focus-beads",
] as const;
export type ItemId = (typeof ITEM_IDS)[number];

/** What pressing an active item can do. */
const ACTIVE_KINDS: readonly EffectKind[] = [
  "time",
  "shield",
  "charge",
  "skip",
  "boost",
  "slow",
  "hints",
];
/** What a worn item can do: applied at the start, or a hook the engine calls. */
const PASSIVE_KINDS: readonly EffectKind[] = ["time", "shield", "hints", "streak", "coins"];

const LevelSchema = z.object({
  /** Kid-facing, e.g. "10 more seconds on the clock". */
  text: z.string().min(1),
  effect: EffectSchema,
  /** Coins to buy this level (level 1) or upgrade to it. */
  price: z.number().int().positive(),
});
export type ItemLevel = z.infer<typeof LevelSchema>;

export const ItemSchema = z
  .object({
    id: z.enum(ITEM_IDS),
    kind: z.enum(ITEM_KINDS),
    name: z.string().min(1),
    /** Level 1 first. */
    levels: z.array(LevelSchema).min(1).max(MAX_LEVEL),
  })
  .refine(
    (it) => it.levels.every((l, i) => i === 0 || l.price > it.levels[i - 1]!.price),
    "each level costs more than the last",
  )
  .refine(
    (it) => new Set(it.levels.map((l) => l.effect.kind)).size === 1,
    "every level of an item does the same kind of thing",
  )
  .refine(
    (it) =>
      it.levels.every((l) =>
        (it.kind === "active" ? ACTIVE_KINDS : PASSIVE_KINDS).includes(l.effect.kind),
      ),
    "an active item is pressed; a passive one applies at the start or on a hook",
  );
export type Item = z.infer<typeof ItemSchema>;

const slow = (seconds: number): Effect => ({ kind: "slow", factor: 0.5, seconds });
const charge = (hits: number): Effect => ({ kind: "charge", hits, times: 2 });
const shield = (hits: number): Effect => ({ kind: "shield", hits });
const hints = (count: number): Effect => ({ kind: "hints", count });
const time = (seconds: number): Effect => ({ kind: "time", seconds });
const streak = (seconds: number): Effect => ({ kind: "streak", every: 5, seconds });
const coins = (share: number): Effect => ({ kind: "coins", share });

const wrongs = (n: number, first: boolean) =>
  n === 1
    ? `The ${first ? "first" : "next"} wrong answer doesn't hurt`
    : `The ${first ? "first" : "next"} ${n} wrong answers don't hurt`;

export const ITEMS: readonly Item[] = z
  .array(ItemSchema)
  .length(ITEM_IDS.length)
  .refine((items) => new Set(items.map((i) => i.id)).size === items.length, "item ids are unique")
  .parse([
    {
      id: "slow-time",
      kind: "active",
      name: "Slow Time",
      levels: [
        { text: "The clock runs at half speed for 15 seconds", effect: slow(15), price: 60 },
        { text: "The clock runs at half speed for 20 seconds", effect: slow(20), price: 90 },
        { text: "The clock runs at half speed for 30 seconds", effect: slow(30), price: 150 },
      ],
    },
    {
      id: "smoke-bomb",
      kind: "active",
      name: "Smoke Bomb",
      levels: [{ text: "Skip this question, no penalty", effect: { kind: "skip" }, price: 25 }],
    },
    {
      id: "double-strike",
      kind: "active",
      name: "Double Strike",
      levels: [
        { text: "The next 5 right answers hit double", effect: charge(5), price: 80 },
        { text: "The next 7 right answers hit double", effect: charge(7), price: 120 },
        { text: "The next 10 right answers hit double", effect: charge(10), price: 200 },
      ],
    },
    {
      id: "shield-scroll",
      kind: "active",
      name: "Shield Scroll",
      levels: [
        { text: wrongs(1, false), effect: shield(1), price: 40 },
        { text: wrongs(2, false), effect: shield(2), price: 60 },
        { text: wrongs(3, false), effect: shield(3), price: 100 },
      ],
    },
    {
      id: "hint-scroll",
      kind: "active",
      name: "Hint Scroll",
      levels: [
        { text: "2 more Show me's this battle", effect: hints(2), price: 40 },
        { text: "3 more Show me's this battle", effect: hints(3), price: 60 },
        { text: "5 more Show me's this battle", effect: hints(5), price: 100 },
      ],
    },
    {
      id: "swift-sandals",
      kind: "passive",
      name: "Swift Sandals",
      levels: [
        { text: "10 more seconds on the clock", effect: time(10), price: 80 },
        { text: "15 more seconds on the clock", effect: time(15), price: 120 },
        { text: "20 more seconds on the clock", effect: time(20), price: 200 },
      ],
    },
    {
      id: "iron-headband",
      kind: "passive",
      name: "Iron Headband",
      levels: [
        { text: wrongs(1, true), effect: shield(1), price: 60 },
        { text: wrongs(2, true), effect: shield(2), price: 90 },
        { text: wrongs(3, true), effect: shield(3), price: 150 },
      ],
    },
    {
      id: "lucky-charm",
      kind: "passive",
      name: "Lucky Charm",
      levels: [
        { text: "20% more coins from a battle", effect: coins(0.2), price: 100 },
        { text: "35% more coins from a battle", effect: coins(0.35), price: 150 },
        { text: "50% more coins from a battle", effect: coins(0.5), price: 250 },
      ],
    },
    {
      id: "focus-beads",
      kind: "passive",
      name: "Focus Beads",
      levels: [
        { text: "Every 5 right answers in a row add 2 seconds", effect: streak(2), price: 60 },
        { text: "Every 5 right answers in a row add 3 seconds", effect: streak(3), price: 90 },
        { text: "Every 5 right answers in a row add 4 seconds", effect: streak(4), price: 150 },
      ],
    },
  ]);

const BY_ID = new Map<string, Item>(ITEMS.map((i) => [i.id, i]));

export function itemById(id: string): Item | undefined {
  return BY_ID.get(id);
}

export function isItemId(id: unknown): id is ItemId {
  return typeof id === "string" && BY_ID.has(id);
}

export function itemsOfKind(kind: ItemKind): Item[] {
  return ITEMS.filter((i) => i.kind === kind);
}

/** The item at a level (1-based), or undefined past its top level. */
export function levelOf(item: Item, level: number): ItemLevel | undefined {
  return item.levels[level - 1];
}

/** The most coins the shop can ever take: every level of every item. */
export function shopTotal(): number {
  return ITEMS.reduce((sum, it) => sum + it.levels.reduce((s, l) => s + l.price, 0), 0);
}
